'use client';

import { motion } from 'motion/react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useTranslations } from 'next-intl';
import type { CategoryConfig, CategoryKey } from './why-reanalyze-data';
import { RICH_TEXT_TAGS, PIE_CELL_STYLE } from './why-reanalyze-data';

type CategoryPieChartProps = {
  categories: readonly CategoryConfig[];
  total: number;
  activeKey: CategoryKey | null;
  activeCategory: CategoryConfig | undefined;
  onHover: (key: CategoryKey) => void;
  onLeave: () => void;
};

export default function CategoryPieChart({
  categories,
  total,
  activeKey,
  activeCategory,
  onHover,
  onLeave,
}: CategoryPieChartProps) {
  const t = useTranslations('Reanalysis.whyReanalyze');

  return (
    <div
      className="relative mx-auto h-[220px] w-[330px] overflow-hidden md:h-[256px] md:w-[350px] lg:mx-0 lg:h-[558px] lg:w-auto lg:max-w-none lg:flex-1 lg:overflow-visible"
      role="img"
      aria-label={categories
        .map(
          cat =>
            `${t.rich(`categories.${cat.key}.name`, RICH_TEXT_TAGS)}: ${cat.value}`,
        )
        .join(', ')}
    >
      {/* Screen reader accessible data */}
      <ul className="sr-only">
        {categories.map(cat => (
          <li key={cat.key}>
            {t.rich(`categories.${cat.key}.name`, RICH_TEXT_TAGS)}: {cat.value}
          </li>
        ))}
      </ul>
      <div className="absolute inset-x-0 -top-[80px] h-[380px] md:-top-[54px] md:h-[352px] lg:inset-0 lg:h-full [&_*]:!outline-none">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={categories as CategoryConfig[]}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="42%"
              outerRadius="62%"
              startAngle={90}
              endAngle={-270}
              stroke="none"
              onMouseLeave={onLeave}
            >
              {categories.map(entry => (
                <Cell
                  key={entry.key}
                  fill={entry.color}
                  opacity={
                    activeKey === null || activeKey === entry.key ? 1 : 0.3
                  }
                  style={PIE_CELL_STYLE}
                  onMouseEnter={() => onHover(entry.key)}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Center text */}
      <div className="pointer-events-none absolute left-1/2 top-[110px] -translate-x-1/2 -translate-y-1/2 text-center md:top-1/2">
        <motion.div
          key={activeKey ?? 'default'}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-[33px] font-bold leading-[34px] tracking-[-0.85px] text-[#0f172b] lg:text-[52px] lg:leading-normal lg:tracking-tight">
            {activeCategory ? activeCategory.value : total}
          </div>
          <div className="flex justify-center">
            <div className="mt-[6px] text-[9px] font-bold uppercase tracking-[0.85px] text-[#90a1b9] lg:mt-[9px] lg:text-[14px] lg:tracking-widest">
              {activeCategory
                ? t.rich(
                    `categories.${activeCategory.key}.name`,
                    RICH_TEXT_TAGS,
                  )
                : t('totalLabel')}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
